import { useGerarPDF } from 'hooks/useGerarPDF';
import useIsMobile from 'hooks/useIsMobile';
import { useCallback } from 'react';
import { Alert, Button, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { enviarArquivo, toWhatsappNumber } from 'utils/evolution';
import { baixarBlobPdf, blobToBase64 } from 'utils/pdfBlob';

export default function ReportDocumento({
  documento,
  gerarBlob,
  fileName,
  titulo,
  nomePaciente,
  iframeTitle,
  emptyMessage,
  erroGeracao,
  sucessoMobile,
  alertaTitulo,
  alertaCorpo,
  whatsapp,
}) {
  const isMobile = useIsMobile();
  const { blob: pdfBlob, url: pdfUrl, loading } = useGerarPDF(documento ? gerarBlob : null);

  const handleDownload = useCallback(() => {
    baixarBlobPdf({
      blob: pdfBlob,
      fileName,
    });
  }, [pdfBlob, fileName]);

  const handleAbrirNovaAba = useCallback(() => {
    const win = window.open(pdfUrl, '_blank');
    if (!win) {
      window.location.href = pdfUrl;
    }
  }, [pdfUrl]);

  async function handleEnviarWhatsapp() {
    const celularWhats = toWhatsappNumber(whatsapp?.celular, '');
    if (!celularWhats) {
      toast.warn('Celular do paciente não cadastrado ou inválido.');
      return;
    }

    try {
      const pdfBase64 = await blobToBase64(pdfBlob);

      const result = await enviarArquivo({
        to: celularWhats,
        pdfBase64,
        paciente: whatsapp?.paciente,
        codLaudo: whatsapp?.codLaudo,
        fileName: whatsapp?.fileName,
        caption: whatsapp?.caption,
      });
      toast.success(result.message);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? err.message ?? 'Erro ao enviar.');
    }
  }

  if (!documento) {
    return (
      <Card className="mt-2">
        <Card.Body>
          <Alert variant="warning" className="mb-0">
            {emptyMessage}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mt-2">
      <Card.Header className="text-center py-3">
        <h5 className="mb-1 fw-bold">{titulo}</h5>
        <small className="text-muted">
          PDF (versão timbrada) — <strong>{nomePaciente}</strong>
        </small>
        <Alert variant="info" className="mb-3 mt-2">
          <div className="fw-bold mb-1">{alertaTitulo}</div>
          <small>{alertaCorpo}</small>
        </Alert>
      </Card.Header>

      <Card.Body className="p-2 p-md-3">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <span className="spinner-border text-primary me-2" />
            <span>Gerando PDF...</span>
          </div>
        ) : pdfUrl && isMobile ? (
          <div className="text-center py-4">
            <div style={{ fontSize: '3rem' }}>📄</div>
            <p className="mt-2 mb-1 fw-semibold">{sucessoMobile}</p>
            <p className="text-muted" style={{ fontSize: '0.88rem' }}>
              Em dispositivos móveis, use os botões abaixo para visualizar, baixar ou enviar o PDF.
            </p>
          </div>
        ) : pdfUrl ? (
          <div
            style={{
              width: '100%',
              aspectRatio: '8.5 / 11',
              maxHeight: '80vh',
            }}
          >
            <iframe
              src={pdfUrl}
              title={iframeTitle}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        ) : (
          <div className="text-center text-muted py-5">{erroGeracao}</div>
        )}
      </Card.Body>

      <Card.Footer className="d-flex flex-wrap justify-content-center gap-2 py-3">
        {isMobile && (
          <Button
            variant="primary"
            onClick={handleAbrirNovaAba}
            disabled={loading || !pdfUrl}
            className="me-1"
          >
            🔍 Visualizar PDF
          </Button>
        )}
        <Button onClick={handleDownload} disabled={loading || !pdfBlob} className="me-1">
          ⬇️ Baixar PDF
        </Button>

        {whatsapp && (
          <Button variant="success" onClick={handleEnviarWhatsapp} disabled={loading || !pdfBlob}>
            <i className="bi bi-whatsapp" style={{ color: '#ffffff', marginRight: 6 }} /> Enviar
            pelo WhatsApp
          </Button>
        )}
      </Card.Footer>
    </Card>
  );
}
